import { useMemo, useState } from 'react';

import { PromocodeStatus, UpdatePromocodeStatus } from '@occc/graphql';
import {
  ConfirmDialog,
  TableRowActionsMenu,
  TableRowActionsMenuItem,
  Typography,
} from '@occc-app/design-system';

type PromocodesGlobalRowActionsProps = {
  promocodeName: string;
  promocodeDetailsUrl: string;
  status: PromocodeStatus;
  onStatusChange(status: UpdatePromocodeStatus): void;
  onDelete(): void;
  isActionEnabled: boolean;
  isDeleteEnabled: boolean;
};

export const PromocodesGlobalRowActions = ({
  promocodeName,
  promocodeDetailsUrl,
  status,
  onStatusChange,
  onDelete,
  isActionEnabled,
  isDeleteEnabled,
}: PromocodesGlobalRowActionsProps) => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const menuItems = useMemo((): TableRowActionsMenuItem[] => {
    const items: TableRowActionsMenuItem[] = [];

    if (status !== PromocodeStatus.Archived) {
      items.push({
        label: 'Archive Code',
        onClick: () => onStatusChange(UpdatePromocodeStatus.Archived),
        disabled: !isActionEnabled,
      });
    } else {
      items.push({
        label: 'Activate Code',
        onClick: () => onStatusChange(UpdatePromocodeStatus.Active),
        disabled: !isActionEnabled,
      });
    }

    items.push({
      label: 'View Details',
      to: promocodeDetailsUrl,
      disabled: !isActionEnabled,
    });

    items.push({
      label: 'Delete Code',
      onClick: () => setConfirmationDialogOpen(true),
      disabled: !isActionEnabled || !isDeleteEnabled,
      sx: { color: 'error.main' },
    });

    return items;
  }, [status, promocodeDetailsUrl, isActionEnabled, isDeleteEnabled, onStatusChange]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} disabled={!isActionEnabled} divided />
      <ConfirmDialog
        dialogTitle='Delete Promo Code?'
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        confirmButtonText='Delete'
        confirmButtonProps={{
          onClick: () => {
            setConfirmationDialogOpen(false);
            onDelete();
          },
          color: 'error',
        }}
      >
        <Typography>This action will delete promo code "{promocodeName}".</Typography>
      </ConfirmDialog>
    </>
  );
};
